import React, { useState, useEffect, useRef } from "react";
import "./styles.css";
import { analyticsHandler } from "./eventLogger";
import axios from "axios";

// Helper: get/set session storage
const SESSION_KEY = "uploadWizardDismissed";
const MODULE_NAME = "35_upload_wizard";
const getDismissed = () => sessionStorage.getItem(SESSION_KEY) === "1";
const setDismissed = () => sessionStorage.setItem(SESSION_KEY, "1");

/// Example user object for demonstration; replace with actual user prop/context as needed
const user = { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York" };

const steps = [
    {
        headline: "Quick Getting Started Guide",
        subcopy: "Before downloading your ThinkorSwim files, review these recommended settings.",
        button: "Continue",
    },
    {
        headline: "Confirm Timezone",
        subcopy:
            "Your timezone affects session reports, risk recognition algorithms, and overall reporting accuracy. Incorrect settings can lead to confusion.",
        dropdown: true,
        info: "ThinkorSwim uses your computer's current timezone when you download files, regardless of when the trades occurred. If the timezone is incorrect, the software may assume trades happened in premarket or aftermarket sessions based on the time difference. This can distort session reports and skew pattern recognition algorithms.",
        button: "Continue",
        link: "Previous",
    },
    {
        headline: "Timezone Conversion",
        subcopy:
            "We recommend converting to EST. It keeps news, alerts, and platform notifications aligned, making it easier to follow market activity.",
        toggle: true,
        badge: "Recommended",
        button: "Continue",
        link: "Previous",
    },
    {
        headline: "Navigate to the Monitor Tab",
        subcopy: "Select 'Account Statement' from the Monitor tab.",
        image: "img/TOS-upload-1.png",
        button: "Continue",
        link: "Previous",
    },
    {
        headline: "Select Transaction Types",
        subcopy: "Check only 'Trades' and 'Start of Day Balances.'",
        info: "'Start of Day Balances' is optional but recommended to fully enable pattern recognition, risk calculations, performance comparisons, and other advanced features.",
        image: "img/TOS-Step-trans-types.png",
        button: "Continue",
        link: "Previous",
    },
    {
        headline: "All Done – Quick Checklist",
        checklist: [
            "Enable Privacy Mode",
            "Trades and Start of Day Balances only",
            "Select date range (3 months max)",
            "Export file",
        ],
        button: null,
        link: "Previous",
    },
];

const TIMEZONES = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney"
];

const TIMEZONES_ABV = [
    "EST",   // America/New_York
    "CST",   // America/Chicago
    "MST",   // America/Denver
    "PST",   // America/Los_Angeles
    "EST",   // America/Toronto (same as New York)
    "GMT",   // Europe/London (or BST in summer)
    "CET",   // Europe/Paris (or CEST in summer)
    "JST",   // Asia/Tokyo
    "AEDT"   // Australia/Sydney (or AEST in winter)
];

// New event handler for wizard navigation buttons
function wizardNavHandler({ action, headline }) {
    return e => {
        analyticsHandler({
            event_type: "button_click",
            module_name: MODULE_NAME,
            subcomponent: `${action} - ${headline}`,
            screen: window.location.pathname
        })(e);
    };
}

// Helper: update user timezone preference via backend
async function updateUserTimezonePreference({ device_timezone, userpref_timezone, convert_timezone }) {
    try {
        const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/update_timezone`,
            {
                device_timezone,
                userpref_timezone,
                convert_timezone,
            },
            { withCredentials: true }
        );
        return res.data;
    } catch (err) {
        console.error("[DEBUG] Timezone update failed:", err.response?.status, err.response?.data);
        return { success: false, message: err.message };
    }
}

const UploadWizard = ({
    activeDate,
    screen,
    deviceTimezone,
    userPrefTimezone,
    onTimezoneChange,
    onClose,
    forceOpen
}) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [timezone, setTimezone] = useState(userPrefTimezone || user.timezone);
    const [convertToEST, setConvertToEST] = useState(timezone === "America/New_York");
    const [tradesChecked, setTradesChecked] = useState(false);
    const [dateChecked, setDateChecked] = useState(false);
    const [exportChecked, setExportChecked] = useState(false);
    const modalRef = useRef(null);

    // Sync external forceOpen prop with internal open state
    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
            setStep(0); // Reset to first step when opening
        }
    }, [forceOpen]);

    // Show wizard for new users only (on first mount)
    useEffect(() => {
        if (!forceOpen && !getDismissed()) {
            setOpen(true);
        }
    }, []);

    // Keyboard: ESC to close
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") handleClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        // Only dismiss if not forceOpen (i.e., not direct link)
        if (!forceOpen) setDismissed();
        if (typeof onClose === "function") {
            onClose();
        }
    };

    // Notify parent when timezone or convertToEST changes
    useEffect(() => {
        if (typeof onTimezoneChange === "function") {
            onTimezoneChange(timezone, convertToEST);
        }
    }, [timezone, convertToEST, onTimezoneChange]);

    // Call backend when timezone or toggle is confirmed
    const handleContinue = async (e) => {
        wizardNavHandler({ action: "Continue", headline: steps[step].headline })(e);

        if (step === 1 || step === 2) {
            const device_timezone = deviceTimezone;
            const userpref_timezone = userPrefTimezone;
            const convert_timezone = convertToEST ? "yes" : "no";

            await updateUserTimezonePreference({
                device_timezone,
                userpref_timezone,
                convert_timezone,
            });
        }

        setStep((s) => s + 1);
    };

    // Update convertToEST if user selects New York in dropdown
    useEffect(() => {
        if (timezone === "America/New_York") {
            setConvertToEST(true);
        }
    }, [timezone]);

    // Handle previous step
    const handlePrevious = (e) => {
        wizardNavHandler({ action: "Previous", headline: steps[step].headline })(e);
        setStep((s) => s - 1);
    };

    if (!open) return null;

    const current = steps[step];

    // Helper for info icon
    const InfoIcon = ({ info }) =>
        info ? (
            <span
                className="tooltip-icon"
                title={info}
                style={{
                    display: "inline-block",
                    marginLeft: 6,
                    fontSize: 18,
                    color: "#1976d2",
                    cursor: "pointer",
                    verticalAlign: "middle",
                }}
            >
                🛈
            </span>
        ) : null;

    return (
        <div
            className="wizard-backdrop"
            data-analytics={MODULE_NAME}
            style={{
                position: "fixed",
                zIndex: 9999,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
            }}
        >
            <div
                className="wizard-modal"
                ref={modalRef}
                style={{
                    background: "#fff",
                    borderRadius: 16,
                    maxWidth: 360,
                    width: "95vw",
                    boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
                    padding: "28px 20px 20px 20px",
                    position: "relative",
                    textAlign: "center",
                }}
            >
                {/* Exit X */}
                <button
                    aria-label="Close"
                    onClick={handleClose}
                    style={{
                        position: "absolute",
                        top: 12,
                        right: 16,
                        background: "none",
                        border: "none",
                        fontSize: 22,
                        color: "#888",
                        cursor: "pointer",
                        zIndex: 2,
                    }}
                >
                    ×
                </button>

                {/* Headline */}
                <div
                    style={{
                        fontWeight: 700,
                        fontSize: 20,
                        marginBottom: 8,
                        color: "#222",
                    }}
                >
                    {current.headline}
                    {current.badge && (
                        <span style={{
                            marginLeft: 8,
                            background: "#e3f2fd",
                            color: "#1976d2",
                            borderRadius: 6,
                            fontSize: 12,
                            padding: "2px 8px",
                            verticalAlign: "middle",
                        }}>
                            {current.badge}
                        </span>
                    )}
                </div>

                {/* Subcopy with info icon at the end */}
                {current.subcopy && (
                    <div style={{ fontSize: 15, color: "#444", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        <span>{current.subcopy}</span>
                        <InfoIcon info={current.info} />
                    </div>
                )}

                {/* Dropdown for timezone */}
                {current.dropdown && (
                    <div style={{ marginBottom: 12 }}>
                        <select
                            value={timezone}
                            id="wizard-timezone-select"
                            onChange={e => setTimezone(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #bbb",
                                fontSize: 15,
                                marginBottom: 8,
                            }}
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz} value={tz}>{tz}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Toggle */}
                {current.toggle && (
                    <div style={{ margin: "16px 0" }}>
                        <label style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 10,
                            fontSize: 15,
                            cursor: "pointer",
                        }}>
                            <span>Convert to EST</span>
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 38,
                                    height: 22,
                                    background: convertToEST ? "#1976d2" : "#ccc",
                                    borderRadius: 12,
                                    position: "relative",
                                    transition: "background 0.2s",
                                }}
                                onClick={() => setConvertToEST(v => !v)}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        left: convertToEST ? 18 : 2,
                                        top: 2,
                                        width: 18,
                                        height: 18,
                                        background: "#fff",
                                        borderRadius: "50%",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
                                        transition: "left 0.2s",
                                    }}
                                />
                            </span>
                            <span style={{
                                marginLeft: 8,
                                color: "#1976d2",
                                fontWeight: 500,
                                fontSize: 13,
                            }}>{convertToEST ? "Yes" : "No"}</span>
                        </label>
                    </div>
                )}

                {/* Image */}
                {current.image && (
                    <div style={{ margin: "16px 0" }}>
                        <img
                            src={current.image}
                            alt=""
                            style={{
                                maxWidth: "100%",
                                borderRadius: 8,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            }}
                        />
                    </div>
                )}

                {/* Checklist */}
                {current.checklist && (
                    <div
                        style={{
                            textAlign: "left",
                            margin: "18px 0 10px 0",
                            fontSize: 16,
                            color: "#222",
                            fontWeight: 500,
                            letterSpacing: "0.01em"
                        }}
                    >
                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                            <input
                                type="checkbox"
                                checked={tradesChecked}
                                onChange={e => setTradesChecked(e.target.checked)}
                            />
                            Trades and Start of Day Balances only
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                            <input
                                type="checkbox"
                                checked={dateChecked}
                                onChange={e => setDateChecked(e.target.checked)}
                            />
                            Select date range (3 months max)
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={exportChecked}
                                onChange={e => setExportChecked(e.target.checked)}
                            />
                            Export file
                        </label>
                    </div>
                )}

                {/* Navigation */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
                    {step > 0 ? (
                        <button
                            className="btn-link"
                            data-step={step}
                            data-analytics={MODULE_NAME}
                            onClick={handlePrevious}
                            style={{
                                background: "none",
                                border: "none",
                                color: "#1976d2",
                                fontWeight: 500,
                                fontSize: 15,
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            Previous
                        </button>
                    ) : <span />}
                    {step < steps.length - 1 && (
                        <button
                            className="btn"
                            data-step={step}
                            data-analytics={MODULE_NAME}
                            onClick={handleContinue}
                            style={{
                                padding: "8px 22px",
                                borderRadius: 6,
                                border: "none",
                                background: "#1976d2",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: 15,
                                cursor: "pointer",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                            }}
                        >
                            {steps[step].button || "Continue"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UploadWizard;